import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import { Check } from 'lucide-react';
import Modal from 'ui/Modal';
import ProfileSettingsButton from 'ui/ProfileSettingsButton';
import UserDisplaynameModal from 'components/User/UserDisplaynameModal';
import UserAboutBriefModal from 'components/User/UserAboutBriefModal';
import UserAboutBackgroundModal from 'components/User/UserAboutBackgroundModal';
import UserAboutIssueModal from 'components/User/UserAboutIssueModal';
import UserPerspectiveForm from 'components/User/UserPerspectiveForm';
import Cookies from 'js-cookie';
import { Progress } from 'ui/ProgressBar';
import { getIssueRankings } from 'util/userUtils';
import api from 'config/axiosConfig';
import { apiRoutes } from "util/apiRoutes";

interface UserOnboardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onTaskUpdated?: () => void;
}

const UserOnboardModal: React.FC<UserOnboardModalProps> = ({ isOpen, onClose, onTaskUpdated }) => {
    const { currentUser, refreshUser, setUserOnboardingTasks, userOnboardInitialView, setUserOnboardInitialView } = useUser();
    
    // Modal visibility states
    const [showDisplaynameModal, setShowDisplaynameModal] = useState<boolean>(false);
    const [showBriefModal, setShowBriefModal] = useState<boolean>(false);
    const [showBackgroundModal, setShowBackgroundModal] = useState<boolean>(false);
    const [showIssueModal, setShowIssueModal] = useState<boolean>(false);
    const [showAlignmentModal, setShowAlignmentModal] = useState<boolean>(false);
    
    // Completion status
    const [dontRemind, setDontRemind] = useState<boolean>(false);
    const [nameCompleted, setNameCompleted] = useState<boolean>(false);
    const [briefCompleted, setBriefCompleted] = useState<boolean>(false);
    const [backgroundCompleted, setBackgroundCompleted] = useState<boolean>(false);
    const [issueCompleted, setIssueCompleted] = useState<boolean>(false);
    const [alignmentCompleted, setAlignmentCompleted] = useState<boolean>(false);
    const [completedTasks, setCompletedTasks] = useState<number>(0);
    const totalTasks = 5;

    // Check completion status based on user data
    useEffect(() => {
        const checkCompletionStatus = async () => {
            if (currentUser) {
                // Display name check
                const hasDisplayName = !!currentUser.display_name && currentUser.display_name.length > 0;
                setNameCompleted(hasDisplayName);
                
                // Brief bio check
                const briefValue = currentUser?.properties?.about_bio_brief?.value;
                const hasBrief = typeof briefValue === 'string' && briefValue.length > 0;
                setBriefCompleted(hasBrief);
                
                // Background check (more than 2 about_bio_ properties, excluding about_bio_brief)
                let aboutBioCount = 0;
                if (currentUser?.properties) {
                    aboutBioCount = Object.keys(currentUser.properties)
                        .filter(key => 
                            key.startsWith('about_bio_') && 
                            key !== 'about_bio_brief' && 
                            currentUser.properties[key]?.value
                        )
                        .length;
                }
                setBackgroundCompleted(aboutBioCount > 2);
                
                // Issue check - check if user has at least 1 issue ranked
                let hasIssue = false;
                try {
                    const { data: issueRankings } = await getIssueRankings();
                    hasIssue = Array.isArray(issueRankings) && issueRankings.length > 2;
                } catch (error) {
                    // If there's an error fetching rankings, fall back to false
                    console.warn('Error fetching issue rankings:', error);
                    hasIssue = false;
                }
                setIssueCompleted(hasIssue);
                
                // Alignment check (at least 3 answers) - use API
                let alignmentAnswersCount = 0;
                try {
                    const alignmentsRes = await api.get(apiRoutes.user.userAlignments(currentUser.id));
                    const relations = alignmentsRes.data.relations || [];
                    
                    if (relations.length > 0) {
                        // Fetch impact details for all relations
                        const impactDetailsPromises = relations.map((r: any) => 
                            api.get(apiRoutes.posts.byId(r.rel_node_id)).catch(e => null)
                        );
                        const impactDetailsResponses = await Promise.all(impactDetailsPromises);
                        
                        // Count valid alignments (those with dimension that's not 'none')
                        alignmentAnswersCount = impactDetailsResponses.filter(res => 
                            res && 
                            res.data && 
                            res.data.properties?.dimension && 
                            res.data.properties.dimension !== 'none'
                        ).length;
                    }
                } catch (error) {
                    console.warn('Error fetching user alignments:', error);
                    // Fall back to 0 if there's an error
                    alignmentAnswersCount = 0;
                }
                setAlignmentCompleted(alignmentAnswersCount >= 3);
                
                // Calculate total completed tasks
                let count = 0;
                if (hasDisplayName) count++;
                if (hasBrief) count++;
                if (aboutBioCount > 2) count++;
                if (hasIssue) count++;
                if (alignmentAnswersCount >= 3) count++;
                
                // Update the tasks count state
                setCompletedTasks(count);
                
                // Update cookie based on the calculated count, not the state
                // This prevents infinite loop since we're not depending on state
                if (count === totalTasks || dontRemind) {
                    Cookies.remove('userOnboardingTasks');
                } else {
                    Cookies.set('userOnboardingTasks', count.toString(), { expires: 30 });
                    setUserOnboardingTasks(count);
                }
            }
        };

        checkCompletionStatus();
    }, [currentUser, setUserOnboardingTasks, dontRemind]);

    // Open directly to the Issues sub-modal when requested (e.g., post-signup)
    useEffect(() => {
        if (isOpen && userOnboardInitialView === 'issues') {
            setShowIssueModal(true);
            // reset so subsequent opens default to main onboarding
            setUserOnboardInitialView(null);
        }
    }, [isOpen, userOnboardInitialView, setUserOnboardInitialView]);
    
    // Separate effect to call onTaskUpdated when completedTasks changes
    useEffect(() => {
        if (onTaskUpdated) {
            onTaskUpdated();
        }
    }, [completedTasks, onTaskUpdated]);

    // Handle modal closings
    const handleModalClose = async () => {
        await refreshUser();
    };

    // Render completion checkmark indicator
    const renderCompletionStatus = (isCompleted: boolean) => {
        if (isCompleted) {
            return (
                <div className="flex items-center justify-center size-6 rounded-full text-green-600">
                    <Check className="size-5" />
                </div>
            );
        }
        return null;
    };

    return (
        <>
            <Modal 
                isOpen={isOpen && !showDisplaynameModal && !showBriefModal && !showBackgroundModal && !showIssueModal && !showAlignmentModal} 
                onClose={onClose} 
                title="Your brew"
            >
                <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                    <div className="flex flex-col flex-grow space-y-4 w-full">
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            Societea emphasizes bringing context to content and conversations. Share information that helps others better understand you. Set other profile information in your <a className="external-link" href="/settings/profile">settings</a>.
                        </p> 
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            Remember that you can keep most things private, if you want.
                        </p> 
                        <Progress 
                            value={((completedTasks) / totalTasks) * 100}
                            className="relative"
                        >
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-eoe-text-meta dark:text-eoe-dark-text-sub z-10">
                                {completedTasks}/{totalTasks}
                            </span>
                        </Progress>

                        <div className="form-control space-y-2">
                            <ProfileSettingsButton
                                title="Name"
                                description="Set your name; this is usually what people call you."
                                onClick={() => setShowDisplaynameModal(true)}
                                settingValue={renderCompletionStatus(nameCompleted)}
                            />
                        
                            <ProfileSettingsButton
                                title="Brief"
                                description="Add a short bio to your profile."
                                onClick={() => setShowBriefModal(true)}
                                settingValue={renderCompletionStatus(briefCompleted)}
                            />
                            
                            <ProfileSettingsButton
                                title="Background"
                                description="Share basic information about yourself. Add at least 3 background details."
                                onClick={() => setShowBackgroundModal(true)}
                                settingValue={renderCompletionStatus(backgroundCompleted)}
                            />
                            
                            <ProfileSettingsButton
                                title="Prioritea (issue rankings)"
                                description="Share what matters most to you: add at least 3 issues."
                                onClick={() => setShowIssueModal(true)}
                                settingValue={renderCompletionStatus(issueCompleted)}
                            />
                            
                            <ProfileSettingsButton
                                title="Perspective"
                                description="Define your social and political views. Respond to at least 3 statements."
                                onClick={() => setShowAlignmentModal(true)}
                                settingValue={renderCompletionStatus(alignmentCompleted)}
                            />
                        </div>
                        <div className="flex flex-row items-center gap-2 justify-start">
                            <span className="flex flex-row justify-start items-center gap-2 text-eoe-text dark:text-eoe-dark-text text-sm">
                                Don't remind me again
                            </span>
                            <input
                                type="checkbox"
                                checked={dontRemind}
                                disabled={false}
                                onChange={(e) => setDontRemind(!dontRemind)}
                                className="toggle-switch-input col-span-1 flex-shrink-0"
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary w-full sm:w-fit py-2 px-4"
                        >
                            Finish Later
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className={`w-full sm:w-fit py-2 px-4 ${
                                completedTasks === totalTasks ? 'btn-primary' : 'btn-primary-disabled'
                            }`}
                            disabled={completedTasks < totalTasks}
                        >
                            {completedTasks === totalTasks ? 'Complete' : `${completedTasks}/${totalTasks} Complete`}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Sub-modals */}
            {showDisplaynameModal && (
                <UserDisplaynameModal 
                    onClose={() => {
                        setShowDisplaynameModal(false);
                        handleModalClose();
                    }} 
                />
            )}

            {showBriefModal && (
                <UserAboutBriefModal 
                    onClose={() => {
                        setShowBriefModal(false);
                        handleModalClose();
                    }} 
                />
            )}

            {showBackgroundModal && (
                <UserAboutBackgroundModal 
                    isOpen={showBackgroundModal}
                    onClose={() => {
                        setShowBackgroundModal(false);
                        handleModalClose();
                    }}
                    initialData={currentUser?.properties}
                    privacy={currentUser?.properties?.privacy_content_default_audience?.value || 'public'}
                />
            )}

            {showIssueModal && (
                <UserAboutIssueModal 
                    onClose={() => {
                        setShowIssueModal(false);
                        handleModalClose();
                    }}
                />
            )}

            {showAlignmentModal && (
                <UserPerspectiveForm 
                    isOpen={showAlignmentModal}
                    onClose={() => {
                        setShowAlignmentModal(false);
                        handleModalClose();
                    }}
                />
            )}
        </>
    );
};

export default UserOnboardModal;
